import { FC, useCallback, useMemo } from "react";
import { HiOutlineShare, HiOutlineHeart, HiHeart } from "react-icons/hi";
import { Resource } from "@modules/for-you/types";
import { useArticleItemControls } from "@modules/for-you/hooks";
import Toast from "@common/components/Toast";
import { RESOURCE_TOPIC_ICON_MAP } from "@modules/for-you/constants.web";
import { Typography } from "@earned/core";
import { themeTextColors } from "@common/constants";
import { useRouter } from "@common/hooks";

export interface ResourceListItemProps {
  article: Resource;
  showIcon: boolean;
}

const ResourceListItem: FC<ResourceListItemProps> = ({ article, showIcon }) => {
  const {
    copyArticleLinkToClipboard,
    toggleHeart,
    description,
    formattedDate,
    isHeartOrUnheartLoading,
  } = useArticleItemControls(article);

  const router = useRouter();
  const navigateToResourceDetailPage = useCallback(() => {
    router.push({
      pathname: `${location.pathname}/resources/${article.ID}`,
      query: { articleTitle: article.NAME },
    });
  }, [article, router]);

  const { IconComponent, label } = useMemo(() => {
    return RESOURCE_TOPIC_ICON_MAP[article.PRIMARY_TOPIC];
  }, [article]);

  return (
    <div className="flex min-h-[232px] min-w-[210px] flex-col gap-3 rounded border border-green-200 bg-white p-4">
      {showIcon && (
        <div className="flex items-center gap-2">
          <IconComponent height={24} width={24} />
          <Typography variant="body" color="secondary">
            {label}
          </Typography>
        </div>
      )}
      <div className="flex h-full flex-col justify-between gap-3">
        <div
          className="flex cursor-pointer flex-col space-y-3"
          onClick={navigateToResourceDetailPage}
        >
          <Typography variant="body" weight="medium" color="primary">
            <span className="line-clamp-2">{article.NAME}</span>
          </Typography>
          {description && (
            <Typography variant="body" color="secondary">
              <span className="line-clamp-3">{description}</span>
            </Typography>
          )}
        </div>
        <div className="flex flex-row items-center gap-4">
          <Typography variant="body-xs" color="secondary">
            {formattedDate}
          </Typography>
          <button
            disabled={isHeartOrUnheartLoading}
            className="flex flex-row items-center"
            onClick={toggleHeart}
          >
            {article.HEARTED ? (
              <HiHeart size={16} color={themeTextColors.secondary} />
            ) : (
              <HiOutlineHeart size={16} color={themeTextColors.secondary} />
            )}
          </button>
          <Toast message="Article link copied to clipboard!" placement="top">
            <div
              className="flex flex-row items-center gap-2"
              onClick={copyArticleLinkToClipboard}
            >
              <HiOutlineShare size={16} color={themeTextColors.secondary} />
              <Typography variant="body-xs" color="primary">
                Share
              </Typography>
            </div>
          </Toast>
        </div>
      </div>
    </div>
  );
};

export default ResourceListItem;
